import React, { useState, useEffect } from 'react';
import { 
  FileText, Plus, Edit2, Copy, 
  X, AlertCircle, FileUp, Loader2, ArrowRight, Shield, ShieldCheck, HelpCircle 
} from 'lucide-react';
import { demandsAPI as apiClient } from '../api';

interface VariableMetadata {
  key: string;
  label: string;
  required: boolean;
  locked?: boolean;
}

interface DocumentTemplate {
  id: string;
  code: string;
  name: string;
  institution: string;
  category: string;
  version: number;
  description?: string;
  status: 'ACTIVE' | 'DRAFT' | 'COMING_SOON' | 'ARCHIVED';
  variables: VariableMetadata[];
  htmlContent?: string;
  sourceFilePath?: string;
  sourceFileVersion?: number;
  previewImage?: string;
  editorMode?: string;
}

export const TemplateManagement: React.FC = () => {
  const [templates, setTemplates] = useState<DocumentTemplate[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // States for Modals/Editors
  const [editingTemplate, setEditingTemplate] = useState<DocumentTemplate | null>(null);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [duplicatingTemplate, setDuplicatingTemplate] = useState<DocumentTemplate | null>(null);

  // Form states
  const [newTemplate, setNewTemplate] = useState({
    code: '',
    name: '',
    institution: 'ALESP',
    category: 'BEM_MATERIAL',
    description: '',
  });

  // Duplication states
  const [dupTarget, setDupTarget] = useState<'VERSION' | 'NEW_CODE'>('VERSION');
  const [dupNewCode, setDupNewCode] = useState('');
  const [dupNewName, setDupNewName] = useState('');

  // Drag and Drop Upload State
  const [uploadFile, setUploadFile] = useState<File | null>(null);
  const [uploadProgress, setUploadProgress] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [dragOver, setDragOver] = useState(false);

  // Variable editor states
  const [newVarKey, setNewVarKey] = useState('');
  const [newVarLabel, setNewVarLabel] = useState('');
  const [newVarRequired, setNewVarRequired] = useState(true);

  // Load templates
  const loadTemplates = async () => {
    setLoading(true);
    try {
      const data = await apiClient.getTemplates();
      setTemplates(data as any);
    } catch (err: any) {
      setError('Erro ao carregar templates oficiais.');
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadTemplates();
  }, []);

  const handleCreate = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      await apiClient.createTemplate({
        ...newTemplate,
        variables: [],
        htmlContent: '',
      });
      setShowCreateModal(false);
      setNewTemplate({ code: '', name: '', institution: 'ALESP', category: 'BEM_MATERIAL', description: '' });
      loadTemplates();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao criar template.');
    }
  };

  const handleStatusChange = async (templateId: string, status: string) => {
    try {
      await apiClient.updateTemplateStatus(templateId, status);
      loadTemplates();
      if (editingTemplate && editingTemplate.id === templateId) {
        setEditingTemplate(prev => prev ? { ...prev, status } as any : null);
      }
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao atualizar status.');
    }
  };

  const handleDuplicate = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!duplicatingTemplate) return;
    try {
      await apiClient.duplicateTemplate(duplicatingTemplate.id, {
        target: dupTarget,
        newCode: dupTarget === 'NEW_CODE' ? dupNewCode : undefined,
        name: dupNewName || undefined
      });
      setDuplicatingTemplate(null);
      setDupNewCode('');
      setDupNewName('');
      loadTemplates();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao duplicar template.');
    }
  };

  // Upload Logic
  const handleDragOver = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(true);
  };

  const handleDragLeave = () => {
    setDragOver(false);
  };

  const validateAndSetFile = (file: File) => {
    const ext = file.name.substring(file.name.lastIndexOf('.')).toLowerCase();
    if (ext !== '.docx' && ext !== '.pdf') {
      setUploadError('Formato inválido. Apenas arquivos .docx (Word) e .pdf são aceitos.');
      return;
    }
    
    // 10 MB limit by default, or configurable
    const maxSize = 10 * 1024 * 1024;
    if (file.size > maxSize) {
      setUploadError('Tamanho de arquivo excedido. Limite máximo: 10MB.');
      return;
    }

    setUploadFile(file);
    setUploadError(null);
  };

  const handleDrop = (e: React.DragEvent) => {
    e.preventDefault();
    setDragOver(false);
    if (e.dataTransfer.files && e.dataTransfer.files[0]) {
      validateAndSetFile(e.dataTransfer.files[0]);
    }
  };

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (e.target.files && e.target.files[0]) {
      validateAndSetFile(e.target.files[0]);
    }
  };

  const executeUpload = async () => {
    if (!editingTemplate || !uploadFile) return;
    setUploadProgress(true);
    setUploadError(null);
    try {
      const response = await apiClient.uploadTemplateSource(editingTemplate.id, uploadFile);
      setEditingTemplate(prev => prev ? { ...prev, sourceFilePath: response.template.sourceFilePath, sourceFileVersion: response.template.sourceFileVersion } as any : null);
      setUploadFile(null);
      alert('Arquivo carregado com sucesso!');
      loadTemplates();
    } catch (err: any) {
      setUploadError(err.response?.data?.error || 'Erro ao fazer upload do arquivo.');
    } finally {
      setUploadProgress(false);
    }
  };

  const handleExtract = async () => {
    if (!editingTemplate) return;
    setLoading(true);
    try {
      const result = await apiClient.extractTemplateContent(editingTemplate.id);
      setEditingTemplate(prev => prev ? { ...prev, htmlContent: result.htmlContent, variables: result.variables } : null);
      alert('Texto e variáveis extraídos com sucesso do arquivo fonte!');
      loadTemplates();
    } catch (err: any) {
      alert(err.response?.data?.error || 'Erro ao extrair conteúdo.');
    } finally {
      setLoading(false);
    }
  };

  const handleSaveVariablesAndHtml = async () => {
    if (!editingTemplate) return;
    try {
      await apiClient.updateTemplate(editingTemplate.id, {
        variables: editingTemplate.variables,
        htmlContent: editingTemplate.htmlContent
      });
      alert('Alterações salvas com sucesso!');
      loadTemplates();
    } catch (err: any) {
      alert('Erro ao salvar alterações.');
    }
  };

  // Variable Management
  const addVariable = () => {
    if (!editingTemplate || !newVarKey.trim() || !newVarLabel.trim()) return;
    const key = newVarKey.trim().toLowerCase();
    
    // Check duplication
    if (editingTemplate.variables.some(v => v.key === key)) {
      alert('Uma variável com esta chave já existe.');
      return;
    }

    const updatedVariables = [...editingTemplate.variables, {
      key,
      label: newVarLabel.trim(),
      required: newVarRequired,
      locked: false
    }];

    setEditingTemplate({ ...editingTemplate, variables: updatedVariables });
    setNewVarKey('');
    setNewVarLabel('');
    setNewVarRequired(true);
  };

  const removeVariable = (key: string) => {
    if (!editingTemplate) return;
    const targetVar = editingTemplate.variables.find(v => v.key === key);
    if (targetVar?.locked) {
      alert('Erro: Variáveis protegidas ([locked: true]) não podem ser excluídas.');
      return;
    }
    const updatedVariables = editingTemplate.variables.filter(v => v.key !== key);
    setEditingTemplate({ ...editingTemplate, variables: updatedVariables });
  };

  const toggleVariableRequired = (key: string) => {
    if (!editingTemplate) return;
    const updatedVariables = editingTemplate.variables.map(v => {
      if (v.key === key) {
        if (v.locked) {
          alert('Erro: A obrigatoriedade de variáveis protegidas não pode ser alterada.');
          return v;
        }
        return { ...v, required: !v.required };
      }
      return v;
    });
    setEditingTemplate({ ...editingTemplate, variables: updatedVariables });
  };

  const getStatusBadge = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-emerald-500/10 text-emerald-400 border border-emerald-500/20">Ativo</span>;
      case 'COMING_SOON':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-amber-500/10 text-amber-400 border border-amber-500/20">Em breve</span>;
      case 'ARCHIVED':
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-rose-500/10 text-rose-400 border border-rose-500/20">Arquivado</span>;
      default:
        return <span className="px-2 py-1 text-xs font-semibold rounded bg-slate-500/10 text-slate-400 border border-slate-500/20">Rascunho</span>;
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto p-4 md:p-6 text-slate-100 font-sans">
      
      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4 border-b border-slate-800 pb-5">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-white flex items-center gap-2">
            <FileText className="text-blue-500" /> Templates Oficiais de Ofício
          </h1>
          <p className="text-sm text-slate-400 mt-1">
            Gerencie, versione e edite os modelos de documentos oficiais de instituições como ALESP, Câmaras e Gabinetes.
          </p>
        </div>
        <button
          onClick={() => setShowCreateModal(true)}
          className="flex items-center gap-2 bg-blue-600 hover:bg-blue-500 text-white font-medium text-sm px-4 py-2.5 rounded-lg transition"
        >
          <Plus className="w-4 h-4" /> Novo Template
        </button>
      </div>

      {loading && !editingTemplate ? (
        <div className="flex flex-col items-center justify-center py-20 text-slate-500">
          <Loader2 className="w-10 h-10 animate-spin text-blue-500 mb-3" />
          Carregando templates...
        </div>
      ) : error ? (
        <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-4 text-red-400 flex items-center gap-2">
          <AlertCircle className="w-5 h-5" /> {error}
        </div>
      ) : !editingTemplate ? (
        
        /* Grid / List of Templates */
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {templates.map(tpl => {
            const isComingSoon = tpl.status === 'COMING_SOON';
            return (
              <div 
                key={tpl.id} 
                className={`bg-slate-900 border rounded-xl overflow-hidden shadow-xl transition-all duration-300 ${
                  isComingSoon ? 'border-amber-500/20 opacity-75' : 'border-slate-800 hover:border-slate-700'
                }`}
              >
                {/* Thumbnail Preview Area */}
                <div className="h-32 bg-gradient-to-br from-slate-950 to-slate-800 flex items-center justify-center border-b border-slate-800 relative">
                  {tpl.previewImage ? (
                    <img src={tpl.previewImage} alt={tpl.name} className="w-full h-full object-cover" />
                  ) : (
                    <FileText className={`w-12 h-12 ${isComingSoon ? 'text-amber-500/40' : 'text-slate-600'}`} />
                  )}
                  <div className="absolute top-3 right-3">
                    {getStatusBadge(tpl.status)}
                  </div>
                </div>

                {/* Content */}
                <div className="p-5 space-y-4">
                  <div>
                    <div className="text-xs font-semibold text-slate-500 uppercase tracking-wider">
                      {tpl.institution} • {tpl.category}
                    </div>
                    <h3 className="text-lg font-bold text-white mt-1 flex items-center justify-between">
                      {tpl.name}
                      <span className="text-xs bg-slate-800 text-slate-400 px-2 py-0.5 rounded">
                        v{tpl.version}
                      </span>
                    </h3>
                    <p className="text-xs text-slate-400 mt-2 line-clamp-2">
                      {tpl.description || 'Sem descrição.'}
                    </p>
                  </div>

                  <div className="border-t border-slate-800/80 pt-4 flex items-center justify-between text-xs text-slate-400">
                    <div>
                      {tpl.variables?.length || 0} variáveis detectadas
                    </div>
                    {tpl.sourceFileVersion && (
                      <div>Fonte: v{tpl.sourceFileVersion}</div>
                    )}
                  </div>

                  {/* Actions */}
                  <div className="flex gap-2 pt-1">
                    <button
                      onClick={() => setEditingTemplate(tpl)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white font-medium text-xs py-2 px-3 rounded-lg transition"
                    >
                      <Edit2 className="w-3.5 h-3.5" /> Editar
                    </button>
                    <button
                      onClick={() => setDuplicatingTemplate(tpl)}
                      className="flex-1 flex items-center justify-center gap-1.5 bg-slate-800 hover:bg-slate-700 text-white font-medium text-xs py-2 px-3 rounded-lg transition"
                    >
                      <Copy className="w-3.5 h-3.5" /> Duplicar
                    </button>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      ) : (
        
        /* Edit Workspace Panel */
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8">
          
          {/* Left panel: Info & variables */}
          <div className="lg:col-span-4 space-y-6">
            <button
              onClick={() => { setEditingTemplate(null); setUploadFile(null); setUploadError(null); }}
              className="text-xs font-semibold text-blue-400 hover:text-blue-300 flex items-center gap-1 transition"
            >
              ← Voltar para listagem
            </button>

            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
              <div>
                <span className="text-xs font-semibold text-slate-500 uppercase tracking-widest">{editingTemplate.institution} v{editingTemplate.version}</span>
                <h2 className="text-xl font-bold text-white mt-1">{editingTemplate.name}</h2>
                <div className="text-xs text-slate-400 mt-1">Status: {getStatusBadge(editingTemplate.status)}</div>
              </div>

              {/* Status Actions */}
              <div className="flex flex-wrap gap-2 pt-2 border-t border-slate-800">
                <button
                  onClick={() => handleStatusChange(editingTemplate.id, 'ACTIVE')}
                  className={`text-xs px-2.5 py-1.5 rounded transition ${editingTemplate.status === 'ACTIVE' ? 'bg-emerald-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                >
                  Ativar
                </button>
                <button
                  onClick={() => handleStatusChange(editingTemplate.id, 'COMING_SOON')}
                  className={`text-xs px-2.5 py-1.5 rounded transition ${editingTemplate.status === 'COMING_SOON' ? 'bg-amber-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                >
                  Em breve
                </button>
                <button
                  onClick={() => handleStatusChange(editingTemplate.id, 'DRAFT')}
                  className={`text-xs px-2.5 py-1.5 rounded transition ${editingTemplate.status === 'DRAFT' ? 'bg-slate-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                >
                  Rascunho
                </button>
                <button
                  onClick={() => handleStatusChange(editingTemplate.id, 'ARCHIVED')}
                  className={`text-xs px-2.5 py-1.5 rounded transition ${editingTemplate.status === 'ARCHIVED' ? 'bg-rose-600 text-white font-semibold' : 'bg-slate-800 text-slate-400 hover:bg-slate-700'}`}
                >
                  Arquivar
                </button>
              </div>
            </div>

            {/* Upload File Zone */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                <FileUp className="w-4 h-4 text-blue-500" /> Atualizar Arquivo Fonte
              </h3>
              
              <div
                onDragOver={handleDragOver}
                onDragLeave={handleDragLeave}
                onDrop={handleDrop}
                className={`border-2 border-dashed rounded-xl p-6 text-center transition cursor-pointer ${
                  dragOver ? 'border-blue-500 bg-blue-500/5' : 'border-slate-800 hover:border-slate-700 bg-slate-950/40'
                }`}
              >
                <input 
                  type="file" 
                  id="template-file" 
                  className="hidden" 
                  onChange={handleFileChange}
                  accept=".docx,.pdf"
                />
                <label htmlFor="template-file" className="cursor-pointer space-y-2 block">
                  <FileUp className="w-8 h-8 mx-auto text-slate-500" />
                  <div className="text-xs text-slate-300">
                    Arrastar arquivo ou <span className="text-blue-500 font-semibold">procurar</span>
                  </div>
                  <div className="text-[10px] text-slate-500">
                    Apenas DOCX ou PDF (Máximo 10MB)
                  </div>
                </label>
              </div>

              {uploadFile && (
                <div className="bg-slate-800/50 rounded-lg p-3 flex items-center justify-between text-xs border border-slate-700">
                  <div className="truncate pr-4 text-slate-300 font-medium">{uploadFile.name}</div>
                  <button 
                    onClick={() => setUploadFile(null)}
                    className="text-slate-500 hover:text-slate-400 transition"
                  >
                    <X className="w-4 h-4" />
                  </button>
                </div>
              )}

              {uploadError && (
                <div className="text-xs text-rose-400 bg-rose-500/10 border border-rose-500/20 rounded p-2.5 flex items-center gap-1.5">
                  <AlertCircle className="w-4 h-4 shrink-0" />
                  {uploadError}
                </div>
              )}

              {uploadFile && (
                <button
                  onClick={executeUpload}
                  disabled={uploadProgress}
                  className="w-full bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs py-2 rounded-lg transition flex items-center justify-center gap-1"
                >
                  {uploadProgress ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : null}
                  Carregar Nova Versão Fonte
                </button>
              )}

              {editingTemplate.sourceFilePath && (
                <div className="flex gap-2">
                  <button
                    onClick={handleExtract}
                    className="w-full border border-slate-700 hover:bg-slate-800 text-slate-300 font-medium text-xs py-2 rounded-lg transition"
                  >
                    Extrair Conteúdo & Variáveis
                  </button>
                </div>
              )}
            </div>

            {/* Variable Editor Zone */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-5 space-y-4">
              <h3 className="text-sm font-bold text-white flex items-center gap-1.5">
                <ShieldCheck className="w-4 h-4 text-emerald-500" /> Variáveis Mapeadas
              </h3>
              
              {/* Add Custom Variable Form */}
              <div className="space-y-3 p-3 bg-slate-950/60 rounded-xl border border-slate-800">
                <div className="text-xs font-semibold text-slate-400">Nova Variável</div>
                <div className="grid grid-cols-2 gap-2">
                  <input
                    type="text"
                    placeholder="chave_ex"
                    value={newVarKey}
                    onChange={e => setNewVarKey(e.target.value)}
                    className="bg-slate-900 border border-slate-800 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none"
                  />
                  <input
                    type="text"
                    placeholder="Legenda"
                    value={newVarLabel}
                    onChange={e => setNewVarLabel(e.target.value)}
                    className="bg-slate-900 border border-slate-800 rounded px-2.5 py-1.5 text-xs text-white focus:outline-none"
                  />
                </div>
                <div className="flex items-center justify-between">
                  <label className="flex items-center gap-1.5 text-xs text-slate-400 select-none cursor-pointer">
                    <input
                      type="checkbox"
                      checked={newVarRequired}
                      onChange={e => setNewVarRequired(e.target.checked)}
                      className="rounded bg-slate-900 border-slate-800 text-blue-600 focus:ring-0"
                    />
                    Obrigatório
                  </label>
                  <button
                    onClick={addVariable}
                    className="bg-emerald-600 hover:bg-emerald-500 text-white font-medium text-[10px] px-3 py-1.5 rounded transition"
                  >
                    Adicionar
                  </button>
                </div>
              </div>

              {/* Variables List */}
              <div className="space-y-2 max-h-60 overflow-y-auto pr-1">
                {editingTemplate.variables?.map(v => (
                  <div 
                    key={v.key} 
                    className={`flex items-center justify-between p-2.5 rounded-lg border text-xs ${
                      v.locked 
                        ? 'bg-slate-950/70 border-slate-800 text-slate-400' 
                        : 'bg-slate-800/30 border-slate-800 text-slate-200'
                    }`}
                  >
                    <div className="space-y-0.5">
                      <div className="flex items-center gap-1.5 font-semibold text-slate-300">
                        {v.locked && <span title="Protegida"><Shield className="w-3.5 h-3.5 text-emerald-500" /></span>}
                        {v.label}
                      </div>
                      <div className="text-[10px] text-slate-500 font-mono">
                        {`{`}{v.key}{`}`} • {v.required ? 'Obrigatória' : 'Opcional'}
                      </div>
                    </div>
                    
                    {!v.locked ? (
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => toggleVariableRequired(v.key)}
                          className="hover:text-blue-400 px-1 py-0.5 rounded transition text-[10px] text-slate-500"
                          title="Alternar Obrigatório"
                        >
                          {v.required ? 'Tornar opcional' : 'Tornar obrigatório'}
                        </button>
                        <button
                          onClick={() => removeVariable(v.key)}
                          className="text-rose-500/80 hover:text-rose-400 p-1 rounded"
                        >
                          <X className="w-4 h-4" />
                        </button>
                      </div>
                    ) : (
                      <span className="text-[10px] text-emerald-500/80 px-2 py-0.5 rounded bg-emerald-500/5 border border-emerald-500/10">Protegida</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>

          {/* Right panel: Template HTML & Sections Preview */}
          <div className="lg:col-span-8 space-y-6">
            
            {/* Editor Mode Config & Sections structure */}
            <div className="bg-slate-900 border border-slate-800 rounded-xl p-6 space-y-5">
              <div className="flex items-center justify-between border-b border-slate-800 pb-4">
                <h3 className="text-base font-bold text-white flex items-center gap-1.5">
                  <FileText className="w-5 h-5 text-blue-500" /> Estrutura do Template & Seções
                </h3>
                <button
                  onClick={handleSaveVariablesAndHtml}
                  className="bg-blue-600 hover:bg-blue-500 text-white font-medium text-xs px-4 py-2 rounded-lg transition"
                >
                  Salvar Alterações
                </button>
              </div>

              {/* Editor Mode select */}
              <div className="grid grid-cols-3 gap-2">
                {['HTML', 'DOCX', 'VISUAL'].map(mode => (
                  <button
                    key={mode}
                    onClick={() => setEditingTemplate({ ...editingTemplate, editorMode: mode } as any)}
                    className={`py-2 px-3 rounded-lg border text-xs font-semibold text-center transition ${
                      editingTemplate.editorMode === mode 
                        ? 'bg-blue-600/10 border-blue-500/30 text-blue-400' 
                        : 'bg-slate-950/40 border-slate-800 text-slate-500 hover:border-slate-700'
                    }`}
                  >
                    Modo {mode}
                  </button>
                ))}
              </div>

              {/* HTML Editor */}
              <div className="space-y-2">
                <div className="flex items-center justify-between">
                  <label className="text-xs font-semibold text-slate-400">Template Base HTML (Variables placeholder)</label>
                  <span className="text-[10px] text-slate-500 flex items-center gap-1">
                    <HelpCircle className="w-3 h-3" /> Insira seções estruturadas para o editor (header, body, footer)
                  </span>
                </div>
                <textarea
                  value={editingTemplate.htmlContent || ''}
                  onChange={e => setEditingTemplate({ ...editingTemplate, htmlContent: e.target.value })}
                  rows={15}
                  placeholder="<div>Insira o HTML base do seu template oficial aqui...</div>"
                  className="w-full bg-slate-950 border border-slate-800 rounded-xl p-4 font-mono text-xs text-slate-300 focus:outline-none focus:border-slate-700"
                />
              </div>

              {/* Layout Visual Simulation (A4 Preview) */}
              <div className="space-y-3">
                <h4 className="text-xs font-bold uppercase tracking-wider text-slate-500">Pré-visualização de Seções</h4>
                <div className="border border-slate-800 rounded-xl overflow-hidden bg-white text-slate-900 p-8 shadow-inner min-h-[400px] flex flex-col font-serif text-sm">
                  {/* Header visual block */}
                  <div className="border-b border-dashed border-slate-200 pb-4 mb-4 select-none opacity-60">
                    <div className="text-[10px] bg-slate-100 text-slate-500 font-mono inline-block px-1.5 py-0.5 rounded mb-1 font-sans">#header (bloqueado)</div>
                    <div className="text-center font-sans text-xs uppercase tracking-wider font-bold">
                      {editingTemplate.institution} • Gabinete do Deputado
                    </div>
                  </div>
                  
                  {/* Body visual block */}
                  <div className="flex-1 py-4">
                    <div className="text-[10px] bg-blue-50 text-blue-500 font-mono inline-block px-1.5 py-0.5 rounded mb-2 font-sans">#body (editável)</div>
                    <div 
                      className="whitespace-pre-wrap leading-relaxed"
                      dangerouslySetInnerHTML={{ 
                        __html: editingTemplate.htmlContent || '<p className="text-slate-400 italic">Nenhum conteúdo HTML configurado para o template base.</p>' 
                      }} 
                    />
                  </div>

                  {/* Signatures visual block */}
                  <div className="mt-8 border-t border-slate-100 pt-4 flex flex-col items-center">
                    <div className="text-[10px] bg-slate-100 text-slate-500 font-mono inline-block px-1.5 py-0.5 rounded mb-2 font-sans">#signature (bloqueado)</div>
                    <div className="w-32 h-10 border border-slate-100 flex items-center justify-center text-xs text-slate-300 font-sans italic bg-slate-50">Assinatura Digital</div>
                    <div className="text-xs font-bold font-sans mt-2">Autoridade Certificada</div>
                  </div>

                  {/* Footer visual block */}
                  <div className="mt-4 pt-4 border-t border-dashed border-slate-200 text-center text-[10px] text-slate-400 font-sans select-none">
                    <div className="text-[10px] bg-slate-100 text-slate-500 font-mono inline-block px-1.5 py-0.5 rounded mb-1 font-sans">#footer (bloqueado)</div>
                    <div>Este documento é gerado oficialmente pelo Gabinete GovFlow.</div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Modal: Create Template */}
      {showCreateModal && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-lg p-6 shadow-2xl relative">
            <button 
              onClick={() => setShowCreateModal(false)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-white mb-4">Novo Template de Documento</h3>
            
            <form onSubmit={handleCreate} className="space-y-4">
              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-400">Código Único (Sem espaços)</label>
                  <input
                    type="text"
                    required
                    placeholder="ALESP_BEM_MATERIAL"
                    value={newTemplate.code}
                    onChange={e => setNewTemplate({ ...newTemplate, code: e.target.value.toUpperCase().replace(/\s+/g, '_') })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none focus:border-slate-700"
                  />
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-400">Nome Amigável</label>
                  <input
                    type="text"
                    required
                    placeholder="Apoio a APAE - Bem Material"
                    value={newTemplate.name}
                    onChange={e => setNewTemplate({ ...newTemplate, name: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none focus:border-slate-700"
                  />
                </div>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-400">Instituição</label>
                  <select
                    value={newTemplate.institution}
                    onChange={e => setNewTemplate({ ...newTemplate, institution: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-slate-700"
                  >
                    <option value="ALESP">ALESP</option>
                    <option value="CAMARA">Câmara dos Deputados</option>
                    <option value="GABINETE">Gabinete</option>
                    <option value="PREFEITURA">Prefeituras</option>
                  </select>
                </div>
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-400">Categoria</label>
                  <select
                    value={newTemplate.category}
                    onChange={e => setNewTemplate({ ...newTemplate, category: e.target.value })}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3 py-2 text-sm text-white focus:outline-none focus:border-slate-700"
                  >
                    <option value="BEM_MATERIAL">Bem Material</option>
                    <option value="RECURSO_FINANCEIRO">Recurso Financeiro</option>
                    <option value="INTERVENCAO">Intervenção</option>
                    <option value="PADRAO">Padrão Geral</option>
                  </select>
                </div>
              </div>

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-400">Descrição</label>
                <textarea
                  placeholder="Descreva a finalidade do template..."
                  value={newTemplate.description}
                  onChange={e => setNewTemplate({ ...newTemplate, description: e.target.value })}
                  rows={3}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none focus:border-slate-700"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm py-2.5 rounded-lg transition mt-2"
              >
                Criar Rascunho
              </button>
            </form>
          </div>
        </div>
      )}

      {/* Modal: Duplicate Template */}
      {duplicatingTemplate && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-950/80 backdrop-blur-sm">
          <div className="bg-slate-900 border border-slate-800 rounded-2xl w-full max-w-md p-6 shadow-2xl relative">
            <button 
              onClick={() => setDuplicatingTemplate(null)}
              className="absolute top-4 right-4 text-slate-400 hover:text-white"
            >
              <X className="w-5 h-5" />
            </button>
            <h3 className="text-lg font-bold text-white mb-2">Duplicar Template</h3>
            <p className="text-xs text-slate-400 mb-4">Escolha a estratégia de duplicação para {duplicatingTemplate.name}.</p>
            
            <form onSubmit={handleDuplicate} className="space-y-4">
              <div className="space-y-2">
                <label className="text-xs font-semibold text-slate-400">Destino</label>
                <div className="grid grid-cols-2 gap-2">
                  <button
                    type="button"
                    onClick={() => setDupTarget('VERSION')}
                    className={`py-2 px-3 rounded-lg border text-xs font-semibold text-center transition ${
                      dupTarget === 'VERSION' 
                        ? 'bg-blue-600/10 border-blue-500/30 text-blue-400' 
                        : 'bg-slate-950/40 border-slate-800 text-slate-500 hover:border-slate-750'
                    }`}
                  >
                    Nova Versão (v{duplicatingTemplate.version + 1})
                  </button>
                  <button
                    type="button"
                    onClick={() => setDupTarget('NEW_CODE')}
                    className={`py-2 px-3 rounded-lg border text-xs font-semibold text-center transition ${
                      dupTarget === 'NEW_CODE' 
                        ? 'bg-blue-600/10 border-blue-500/30 text-blue-400' 
                        : 'bg-slate-950/40 border-slate-800 text-slate-500 hover:border-slate-750'
                    }`}
                  >
                    Novo Código / Instituição
                  </button>
                </div>
              </div>

              {dupTarget === 'NEW_CODE' && (
                <div className="space-y-1.5">
                  <label className="text-xs font-semibold text-slate-400">Novo Código (Sem espaços)</label>
                  <input
                    type="text"
                    required
                    placeholder="EX: CAMARA_BEM_MATERIAL"
                    value={dupNewCode}
                    onChange={e => setDupNewCode(e.target.value.toUpperCase().replace(/\s+/g, '_'))}
                    className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none focus:border-slate-700"
                  />
                </div>
              )}

              <div className="space-y-1.5">
                <label className="text-xs font-semibold text-slate-400">Novo Nome do Modelo (Opcional)</label>
                <input
                  type="text"
                  placeholder={`${duplicatingTemplate.name} (Cópia)`}
                  value={dupNewName}
                  onChange={e => setDupNewName(e.target.value)}
                  className="w-full bg-slate-950 border border-slate-800 rounded-lg px-3.5 py-2 text-sm text-white focus:outline-none focus:border-slate-700"
                />
              </div>

              <button
                type="submit"
                className="w-full bg-blue-600 hover:bg-blue-500 text-white font-semibold text-sm py-2.5 rounded-lg transition mt-2 flex items-center justify-center gap-1.5"
              >
                Duplicar Modelo <ArrowRight className="w-4 h-4" />
              </button>
            </form>
          </div>
        </div>
      )}

    </div>
  );
};
